// src/pages/Home.jsx
import React from "react";
import { Link } from "react-scroll";
import Services from "./Services";
import About from "./About";
import Contact from "./Contact";
import Meta from "../components/Meta";

export default function Home() {
  return (
    <div className="bg-white text-gray-900">
      {/* --- SEO HEAD (invisible; does not change your design/language) --- */}
      <Meta
        title="PJG Service – Société de nettoyage à Paris et Île-de-France"
        description="Nettoyage de bureaux, ménage à domicile, vitres et fin de chantier. Devis rapide, équipe fiable à Paris et en Île-de-France."
        path="/"
        primaryDomain="https://pjgservice.fr"
        alternates={[
          { hrefLang: "fr-FR", href: "https://pjgservice.fr/" },
          { hrefLang: "en", href: "https://pjgservice.com/" },
          { hrefLang: "x-default", href: "https://pjgservice.fr/" }
        ]}
        localBusiness={{
          name: "PJG Service",
          telephone: "+33 6 12 34 56 78",
          priceRange: "€€",
          streetAddress: "12 Rue Exemple",
          postalCode: "75010",
          addressLocality: "Paris",
          addressRegion: "Île-de-France",
          addressCountry: "FR",
          areaServed: ["Paris", "Île-de-France"],
          opens: "08:30",
          closes: "18:00",
          opensSat: "09:00",
          closesSat: "14:00",
          logo: "https://pjgservice.fr/pjg-logo-512.png",
          image: "https://pjgservice.fr/pjg-og.png",
        }}
        breadcrumbs={[{ name: "Accueil", url: "/" }]}
        faq={[
          { q: "Intervenez-vous le samedi ?", a: "Oui, jusqu’à 14h." },
          { q: "Proposez-vous des contrats mensuels ?", a: "Oui, tarifs dégressifs pour les pros." }
        ]}
      />

      {/* Invisible crawlable anchors for bots (no visual change) */}
      <nav aria-label="in-page" className="sr-only">
        <a href="#home">Maison</a> • <a href="#services">Services</a> • <a href="#about">À propos</a> • <a href="#contact">Contact</a>
      </nav>

      {/* Navbar (unchanged visually) */}
      <header className="fixed top-0 left-0 w-full z-50 flex justify-between items-center px-8 py-4 bg-gray-100 shadow">
        <div className="flex items-center space-x-2">
          <img src="/images/logo.png" alt="PJG Service" className="w-16 h-16 rounded" />
          <span className="font-bold text-lg text-gray-900">PJG Service</span>
        </div>

        <nav className="hidden md:flex space-x-8 text-sm font-medium text-gray-800">
          <Link to="home" smooth={true} duration={500} className="cursor-pointer hover:text-blue-600">Maison</Link>
          <Link to="services" smooth={true} duration={500} className="cursor-pointer hover:text-blue-600">Services</Link>
          <Link to="about" smooth={true} duration={500} className="cursor-pointer hover:text-blue-600">À propos</Link>
          <Link to="contact" smooth={true} duration={500} className="cursor-pointer hover:text-blue-600">Contact</Link>
        </nav>

        <Link
          to="contact"
          smooth={true}
          duration={500}
          className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800 cursor-pointer"
        >
          Réservez maintenant
        </Link>
      </header>

      {/* Hero Section (only technical attrs on img for performance) */}
      <section
        id="home"
        className="flex flex-col md:flex-row items-center justify-between bg-blue-900 text-white px-8 md:px-16 py-20 min-h-screen pt-28"
      >
        <div className="md:w-1/2 space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">
            Nettoyage professionnel <br />
            <span className="text-blue-300">Simplifié</span>
          </h1>
          <p className="text-lg text-gray-200 max-w-md">
            Transformez votre espace avec les solutions de nettoyage expertes de PJG Service.
            Fiable, complet et écologique pour les maisons et les bureaux.
          </p>

          {/* CTA */}
          <div className="flex space-x-4">
            <a href="#quote" className="bg-white text-blue-900 font-medium px-5 py-3 rounded shadow hover:bg-gray-200">
              Obtenez un devis gratuit
            </a>
            <a href="#learn" className="border border-white px-5 py-3 rounded font-medium hover:bg-white hover:text-blue-900">
              Apprendre encore plus
            </a>
          </div>

          {/* Ratings */}
          <div className="flex items-center space-x-6 text-sm text-gray-200 pt-4">
            <div className="flex items-center space-x-1">
              <span className="text-yellow-400">★★★★★</span>
              <span>4.9/5 Notation</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-green-400">✔</span>
              <span>500+ Clients satisfaits</span>
            </div>
          </div>
        </div>

        {/* Image (no visual change) */}
        <div className="md:w-1/2 mt-10 md:mt-0">
          <img
            src="https://images.unsplash.com/photo-1557804506-669a67965ba0"
            alt="Office"
            width="1200"
            height="800"
            fetchpriority="high"
            className="rounded-lg shadow-lg"
          />
        </div>
      </section>

      {/* Imported Sections with IDs for scroll */}
      <section id="services"><Services /></section>
      <section id="about"><About /></section>
      <section id="contact"><Contact /></section>

      {/* Footer (unchanged) */}
      <footer className="bg-blue-950 text-gray-200 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10">
          {/* Logo + About */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <img src="/images/footer.jpeg" alt="PJG Service" className="w-16 h-16 rounded" />
              <span className="font-bold text-lg">PJG Service</span>
            </div>
            <p className="text-sm leading-relaxed mb-4">
              PJG Service vous propose des services de nettoyage professionnels et fiables. Nous redonnons tout leur éclat à vos espaces grâce à nos solutions écologiques et à notre équipe d'experts.
            </p>
          </div>

          {/* Services list */}
          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="services" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Nettoyage résidentiel
                </Link>
              </li>
              <li>
                <Link to="services" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Nettoyage commercial
                </Link>
              </li>
              <li>
                <Link to="services" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Nettoyage en profondeur
                </Link>
              </li>
              <li>
                <Link to="services" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Nettoyage avant/après déménagement
                </Link>
              </li>
              <li>
                <Link to="services" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Nettoyage post-construction
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="about" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  À propos de nous
                </Link>
              </li>
              <li>
                <Link to="about" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Notre équipe
                </Link>
              </li>
              <li>
                <Link to="about" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Carrières
                </Link>
              </li>
              <li>
                <Link to="about" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Avis
                </Link>
              </li>
              <li>
                <Link to="about" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold mb-4">Soutien</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="contact" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Contactez-nous
                </Link>
              </li>
              <li>
                <Link to="contact" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Politique de confidentialité
                </Link>
              </li>
              <li>
                <Link to="contact" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Conditions d'utilisation
                </Link>
              </li>
              <li>
                <Link to="contact" smooth={true} duration={500} className="cursor-pointer hover:text-white">
                  Politique de cookies
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 border-t border-gray-700 pt-6 flex flex-col md:flex-row justify-center items-center text-base px-6 max-w-7xl mx-auto">
          <p className="text-center md:text-left">
            © {new Date().getFullYear()} PJG Services. Tous droits réservés.
            <span className="mx-2">•</span>
            <a
              href="https://codebytz.com"
              target="_blank"
              rel="noreferrer"
              className="font-bold group"
            >
              <span className="text-white">Développé par </span>
              <span className="text-teal-500 group-hover:text-teal-400">CODEBYTZ TECHNOLOGIES</span>
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
